import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";

export interface PinStatus {
  hasPinSet: boolean;
  isSessionValid: boolean;
  pinSetAt: string | null;
}

interface VerifyPinResult {
  success: boolean;
  error?: string;
  lockedUntil?: string;
  remainingAttempts?: number;
}

interface PinStatusResult {
  hasPinSet: boolean;
  pinSetAt?: string;
  isLocked?: boolean;
}

const PIN_SESSION_KEY = "pin_session_token";
const PIN_SESSION_EXPIRY_KEY = "pin_session_expiry";
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

const PIN_FALLBACK_HASH_KEY = "pin_fallback_hash";
const PIN_FALLBACK_SET_AT_KEY = "pin_fallback_set_at";

function fallbackKey(userId: string, suffix: string): string {
  return `${suffix}_${userId}`;
}

function isRpcNotConfigured(error: any): boolean {
  const code = error?.code;
  const msg = typeof error?.message === "string" ? error.message : "";
  return (
    code === "42883" ||
    code === 42883 ||
    code === "PGRST202" || // PostgREST: function not found in schema cache
    msg.includes("function") ||
    msg.includes("does not exist") ||
    msg.includes("digest") // server-side pgcrypto digest() type mismatch → use fallback
  );
}

async function hashPin(userId: string, pin: string): Promise<string> {
  const data = new TextEncoder().encode(userId + ":" + pin);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

class PinService {
  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated. Please log in.");
    }
    return session.user.id;
  }

  private isSessionValid(): boolean {
    if (typeof window === "undefined") return false;
    
    const expiryStr = localStorage.getItem(PIN_SESSION_EXPIRY_KEY);
    if (!expiryStr) return false;
    
    const expiry = parseInt(expiryStr, 10);
    return Date.now() < expiry;
  }

  private setSessionValid(): void {
    if (typeof window === "undefined") return;
    
    const expiry = Date.now() + SESSION_DURATION_MS;
    localStorage.setItem(PIN_SESSION_KEY, "valid");
    localStorage.setItem(PIN_SESSION_EXPIRY_KEY, expiry.toString());
  }

  private clearSession(): void {
    if (typeof window === "undefined") return;
    
    localStorage.removeItem(PIN_SESSION_KEY);
    localStorage.removeItem(PIN_SESSION_EXPIRY_KEY);
  }

  private getStatusFallback(userId: string): PinStatus {
    if (typeof window === "undefined") {
      return { hasPinSet: false, isSessionValid: false, pinSetAt: null };
    }
    const hasPinSet = !!localStorage.getItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY));
    const pinSetAt = hasPinSet ? localStorage.getItem(fallbackKey(userId, PIN_FALLBACK_SET_AT_KEY)) || null : null;
    return {
      hasPinSet,
      isSessionValid: this.isSessionValid(),
      pinSetAt,
    };
  }

  async getStatus(): Promise<PinStatus> {
    const RPC_TIMEOUT_MS = 10_000; // 10s then fallback so UI doesn't hang
    let userId: string | null = null;
    try {
      userId = await this.getCurrentUserId();
      const rpcPromise = supabase.rpc("get_pin_status", {
        p_user_id: userId
      }) as Promise<{ data: PinStatusResult | null; error: any }>;
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("PIN_RPC_TIMEOUT")), RPC_TIMEOUT_MS)
      );

      const { data, error } = await Promise.race([rpcPromise, timeoutPromise]);

      if (error) {
        if (isRpcNotConfigured(error)) {
          return this.getStatusFallback(userId);
        }
        console.error("Error fetching PIN status:", error);
        throw new Error(`Failed to get PIN status: ${error.message}`);
      }

      return {
        hasPinSet: data?.hasPinSet || false,
        isSessionValid: this.isSessionValid(),
        pinSetAt: data?.pinSetAt || null,
      };
    } catch (err: any) {
      if (err?.message?.includes("not authenticated")) {
        return {
          hasPinSet: false,
          isSessionValid: false,
          pinSetAt: null,
        };
      }
      // Timeout or network error (ERR_TIMED_OUT, etc.) → use fallback so app still works
      if (
        userId &&
        (err?.message === "PIN_RPC_TIMEOUT" ||
          err?.name === "AbortError" ||
          (typeof err?.message === "string" &&
            (err.message.includes("timeout") ||
              err.message.includes("network") ||
              err.message.includes("Failed to fetch"))))
      ) {
        return this.getStatusFallback(userId);
      }
      throw err;
    }
  }

  async setPin(newPin: string, currentPin?: string): Promise<{ success: boolean }> {
    const userId = await this.getCurrentUserId();

    if (!/^\d{4,8}$/.test(newPin)) {
      throw new Error("PIN must be 4-8 digits");
    }

    const status = await this.getStatus();
    if (status.hasPinSet) {
      if (!currentPin) {
        throw new Error("Current PIN is required to change PIN");
      }
      
      const verifyResult = await this.verifyPin(currentPin);
      if (!verifyResult.success) {
        throw new Error("Current PIN is incorrect");
      }
    }

    const { error } = await supabase.rpc("set_user_pin", {
      p_user_id: userId,
      p_pin: newPin
    });

    if (error) {
      console.error("Error setting PIN:", error);
      if (isRpcNotConfigured(error)) {
        const hash = await hashPin(userId, newPin);
        if (typeof window !== "undefined") {
          localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY), hash);
          localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_SET_AT_KEY), new Date().toISOString());
        }
        this.setSessionValid();
        return { success: true };
      }
      throw new Error(`Failed to set PIN: ${error.message}`);
    }

    // Sync to localStorage so when get_pin_status times out, fallback still sees "has PIN" and can verify
    const hash = await hashPin(userId, newPin);
    if (typeof window !== "undefined") {
      localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY), hash);
      localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_SET_AT_KEY), new Date().toISOString());
    }
    this.setSessionValid();
    return { success: true };
  }

  async verifyPin(pin: string): Promise<{ success: boolean }> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase.rpc("verify_user_pin", {
      p_user_id: userId,
      p_pin: pin
    }) as { data: VerifyPinResult | null; error: any };

    if (error) {
      console.error("Error verifying PIN:", error);
      if (isRpcNotConfigured(error)) {
        return this.verifyPinFallback(userId, pin);
      }
      throw new Error(`Failed to verify PIN: ${error.message}`);
    }

    if (!data) {
      throw new Error("Failed to verify PIN: No response");
    }

    if (data.success) {
      this.setSessionValid();
      return { success: true };
    }

    if (data.error === "no_pin_set") {
      throw new Error("No PIN has been set");
    }

    if (data.error === "account_locked") {
      const lockedUntil = data.lockedUntil ? new Date(data.lockedUntil) : null;
      if (lockedUntil) {
        const remainingTime = Math.ceil((lockedUntil.getTime() - Date.now()) / 60000);
        throw new Error(`Too many failed attempts. Try again in ${remainingTime} minute(s).`);
      }
      throw new Error("Account is locked due to too many failed attempts.");
    }

    if (data.error === "invalid_pin") {
      const remaining = data.remainingAttempts ?? 0;
      throw new Error(`Incorrect PIN. ${remaining} attempt(s) remaining.`);
    }

    throw new Error("Incorrect PIN");
  }

  private async verifyPinFallback(userId: string, pin: string): Promise<{ success: boolean }> {
    if (typeof window === "undefined") {
      throw new Error("No PIN has been set");
    }
    const storedHash = localStorage.getItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY));
    if (!storedHash) {
      throw new Error("No PIN has been set");
    }
    const inputHash = await hashPin(userId, pin);
    if (inputHash !== storedHash) {
      throw new Error("Incorrect PIN");
    }
    this.setSessionValid();
    return { success: true };
  }

  async removePin(currentPin: string): Promise<{ success: boolean }> {
    const userId = await this.getCurrentUserId();

    await this.verifyPin(currentPin);

    const { error } = await supabase.rpc("remove_user_pin", {
      p_user_id: userId
    });

    if (error) {
      console.error("Error removing PIN:", error);
      if (isRpcNotConfigured(error)) {
        if (typeof window !== "undefined") {
          localStorage.removeItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY));
          localStorage.removeItem(fallbackKey(userId, PIN_FALLBACK_SET_AT_KEY));
        }
        this.clearSession();
        return { success: true };
      }
      throw new Error(`Failed to remove PIN: ${error.message}`);
    }

    this.clearSession();
    return { success: true };
  }

  invalidateSession(): void {
    this.clearSession();
  }

  /** Verify account password (re-auth). Used to change PIN without current PIN. */
  async verifyAccountPassword(password: string): Promise<{ success: boolean }> {
    const session = await getCurrentSession();
    if (!session?.user?.email) {
      throw new Error("Session not found. Please log in again.");
    }
    const { error } = await supabase.auth.signInWithPassword({
      email: session.user.email,
      password,
    });
    if (error) {
      if (error.message?.toLowerCase().includes("invalid login")) {
        throw new Error("Incorrect password.");
      }
      throw new Error(error.message || "Failed to verify password.");
    }
    return { success: true };
  }

  /** Set new PIN without requiring current PIN. Only use after verifyAccountPassword. */
  async setPinWithoutCurrentPin(newPin: string): Promise<{ success: boolean }> {
    const userId = await this.getCurrentUserId();
    if (!/^\d{4,8}$/.test(newPin)) {
      throw new Error("PIN must be 4-8 digits");
    }
    const { error } = await supabase.rpc("set_user_pin", {
      p_user_id: userId,
      p_pin: newPin,
    });
    if (error) {
      console.error("Error setting PIN:", error);
      if (isRpcNotConfigured(error)) {
        const hash = await hashPin(userId, newPin);
        if (typeof window !== "undefined") {
          localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY), hash);
          localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_SET_AT_KEY), new Date().toISOString());
        }
        this.setSessionValid();
        return { success: true };
      }
      throw new Error(`Failed to set PIN: ${error.message}`);
    }
    const hash = await hashPin(userId, newPin);
    if (typeof window !== "undefined") {
      localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_HASH_KEY), hash);
      localStorage.setItem(fallbackKey(userId, PIN_FALLBACK_SET_AT_KEY), new Date().toISOString());
    }
    this.setSessionValid();
    return { success: true };
  }
}

export const pinService = new PinService();
